/**
 * ========================================
 * LIVE CLASS WEBSOCKET CONTEXT
 * ========================================
 *
 * Holds a single LiveClassWebSocketService instance for the duration of a
 * live-class session (teacher or student). Wrapping the route with this
 * provider means the WebSocket survives sub-component re-renders and avoids
 * unnecessary reconnects if child components mount/unmount.
 *
 * Usage in AppRouter:
 *   <LiveClassWSProvider><LiveClass /></LiveClassWSProvider>
 *   <LiveClassWSProvider><StudentLiveClass /></LiveClassWSProvider>
 *
 * The service is disposed when the provider unmounts (i.e. the user
 * navigates away from the live-class route entirely).
 */

import React, { createContext, useContext, useRef, useEffect } from 'react';
import { LiveClassWebSocketService } from '@/services/live-class/LiveClassWebSocket';

export interface LiveClassWSContextValue {
  /** Shared service ref — consumers read/write this ref directly */
  wsRef: React.MutableRefObject<LiveClassWebSocketService | null>;
}

const LiveClassWSContext = createContext<LiveClassWSContextValue | null>(null);

export const LiveClassWSProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const wsRef = useRef<LiveClassWebSocketService | null>(null);

  // Dispose the service when the provider (route) unmounts
  useEffect(() => {
    return () => {
      wsRef.current?.dispose();
      wsRef.current = null;
    };
  }, []);

  return (
    <LiveClassWSContext.Provider value={{ wsRef }}>
      {children}
    </LiveClassWSContext.Provider>
  );
};

/** Returns the context value if used inside a LiveClassWSProvider, else null */
export const useLiveClassWSContext = (): LiveClassWSContextValue | null =>
  useContext(LiveClassWSContext);
